import React, {useState} from 'react';
import Typography from '@mui/material/Typography';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {API_URL} from '../../constants';
import PrimaryButton from '../StyledComponents/PrimaryButton';
import BidmiiTextField from '../StyledComponents/BidmiiTextField';
import {theme} from '../../Theme';
import SecondaryButton from '../StyledComponents/SecondaryButton';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Rating from '@mui/material/Rating';
import BidmiiSelect from '../StyledComponents/BidmiiSelect';
import {withStyles} from '@mui/styles';
import StandardDialog from '../StyledComponents/StandardDialog';
import * as Sentry from '@sentry/react';
import useSnackbar from '../../hooks/useSnackbar';

const StyledRating = withStyles({
  icon: {
    fontSize: 48,
    margin: window.innerWidth > 600 ? '0 4px' : 0,
  },
  iconFilled: {
    color: theme.palette.blue.main,
  },
  iconHover: {
    color: theme.palette.blue.main,
  },
})(Rating);

const feedbackTypes = [
  {label: 'Review', value: 'REVIEW'},
  {label: 'Report a bug', value: 'BUG_REPORT'},
  {label: 'Suggest an improvement', value: 'SUGGESTION'},
];

const platformAreas = [
  {label: 'Project creation', value: 'Project creation'},
  {label: 'Quote creation', value: 'Quote creation'},
  {label: 'Project awarding', value: 'Project awarding'},
  {label: 'Project / stage completion', value: 'Project / stage completion'},
  {label: 'Scheduling a site visit', value: 'Scheduling a site visit'},
  {label: 'Q & A', value: 'Q & A'},
  {label: 'Direct messaging', value: 'Direct Messaging'},
  {label: 'Edit profile', value: 'Edit profile'},
  {label: 'Other / General', value: 'Other / General'},
];

export default function FeedbackDialog(props: $TSFixMe) {
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const {openSnackbar} = useSnackbar();

  const [feedbackType, setFeedbackType] = useState('REVIEW');
  const [area, setArea] = useState('Project creation');
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);

  const onSubmit = () => {
    const body = {
      note: feedback,
      feedbackType,
      feedbackContext: area,
      rating: feedbackRating,
    };

    setLoading(true);
    securedFetchRequest(`${API_URL}/userNotes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        openSnackbar('Thank you for providing your feedback!');
        props.handleClose();
      })
      .catch((err) => {
        setLoading(false);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'FeedbackDialog.tsx onSubmit()');
          Sentry.captureException(err);
        });
      });
  };

  return (
    <StandardDialog
      open={true}
      hideCloseButton
      onClose={props.handleClose}
      loading={loading}
      secondaryAction={
        <SecondaryButton onClick={props.handleClose}>CANCEL</SecondaryButton>
      }
      primaryAction={
        <PrimaryButton onClick={onSubmit} disabled={!feedback}>
          SUBMIT
        </PrimaryButton>
      }>
      <div>
        <Typography variant="body1" style={{fontSize: 22}} gutterBottom>
          Help us improve.
        </Typography>
        <Typography
          variant="subtitle1"
          style={{color: theme.palette.primary.light, lineHeight: 1.3}}>
          Do you have feedback for us? Please share it with us to help us
          improve.
        </Typography>

        <Box mt={3}>
          {feedbackType === 'REVIEW' && (
            <Grid container justifyContent="center" style={{marginBottom: 16}}>
              <StyledRating
                name={props.type}
                precision={1}
                value={feedbackRating}
                onChange={(e, newValue) => setFeedbackRating(newValue)}
              />
            </Grid>
          )}
          <BidmiiSelect
            fullWidth
            gutterBottom
            label="Type of feedback"
            items={feedbackTypes}
            value={feedbackType}
            onChange={(e: $TSFixMe) => setFeedbackType(e.target.value)}
          />
          <BidmiiSelect
            fullWidth
            gutterBottom
            label="Area"
            items={platformAreas}
            value={area}
            onChange={(e: $TSFixMe) => setArea(e.target.value)}
          />
          <BidmiiTextField
            label="Comments*"
            placeholder="Tell us about your experience and how we can improve!"
            multiline
            rows={5}
            fullWidth
            onChange={(event) => {
              setFeedback(event.currentTarget.value);
            }}
          />
        </Box>
      </div>
    </StandardDialog>
  );
}
